// Pure derivation of whether a realty workflow record is buy-side or
// sell-side, plus its display label.
//
// Nate's feedback (2026-06-29, 02:24 & 07:29): "these guys are buyers, I
// don't think that's annotated." Realty is the one company whose work
// splits into two opposite sides — representing a SELLER (a Listing) vs
// representing a BUYER (a Home Purchase) — and the Processing list +
// detail headers gave no hint which side a given record was.
//
// The side is NOT stored as its own column. It's encoded across the
// signals the workflow records already carry, and which one is present
// depends on where in the lifecycle the record sits:
//
//   * Opportunity:  opportunity_type  — 'listing_consultation' (sell) vs
//                                        'buyer_consultation'  (buy).
//   * Transaction:  the LINKED service_record's service_type /
//                   service_family — 'listing' (sell) vs 'home_purchase'
//                   (buy). transaction_type itself is just
//                   'realty_transaction' for both sides, so it can't tell
//                   them apart. (See OPPORTUNITY_TYPE_DEFAULTS in
//                   opportunity-transitions.ts — both realty opportunity
//                   types convert to the same transaction_type.)
//
// This helper takes whatever signals a caller has and returns the side,
// or null when the record isn't realty / the side can't be determined.
// Kept pure (no DB, no I/O) so it unit-tests in the Node pool and both
// the server (detail/list queries) and the UI agree on the mapping.
//
// CONSUMER: internal-ui

import { humanizeServiceType } from "../db/service-type-labels";
import {
  LANDLORD_CONSULTATION,
  LANDLORD_RENTAL_SERVICE_TYPE,
  TENANT_CONSULTATION,
  TENANT_RENTAL_SERVICE_TYPE,
  isRentalServiceType,
  rentalNoun,
} from "./rental";

export type RealtySide = "buy" | "sell";

// Sell-side signals (representing a seller — a Listing). The defensive
// 'realty_listing' / 'listing_sale' entries cover any import path that
// might set a more specific transaction_type than 'realty_transaction'.
const SELL_SIGNALS = new Set([
  "listing_consultation", // opportunity_type
  "listing", // service_type / service_family
  "realty_listing", // defensive transaction_type
  // Rentals (2026-09-22): representing a LANDLORD is the sell side.
  LANDLORD_CONSULTATION, // opportunity_type
  LANDLORD_RENTAL_SERVICE_TYPE, // service_type
]);

// Buy-side signals (representing a buyer — a Home Purchase).
const BUY_SIGNALS = new Set([
  "buyer_consultation", // opportunity_type
  "home_purchase", // service_type / service_family
  "realty_purchase", // defensive transaction_type
  // Rentals (2026-09-22): representing a TENANT is the buy side.
  TENANT_CONSULTATION, // opportunity_type
  TENANT_RENTAL_SERVICE_TYPE, // service_type
]);

export interface RealtySideSignals {
  /** Owning company slug. When known and not 'realty', the result is null. */
  companySlug?: string | null;
  /** opportunities.opportunity_type (buy/sell encoded directly). */
  opportunityType?: string | null;
  /** transactions.transaction_type ('realty_transaction' — no side on its own). */
  transactionType?: string | null;
  /** Linked service_record service_type ('listing' | 'home_purchase'). */
  serviceType?: string | null;
  /** Linked service_record service_family ('listing' | 'home_purchase'). */
  serviceFamily?: string | null;
}

/**
 * Derive buy-side vs sell-side for a realty record, or null if it isn't
 * realty / the side can't be told from the available signals.
 */
export function realtySide(signals: RealtySideSignals): RealtySide | null {
  // Only realty has a buy/sell side. When the company is known and isn't
  // realty, short-circuit so a stray 'listing'-shaped value elsewhere can
  // never be misread as a side.
  if (signals.companySlug && signals.companySlug !== "realty") return null;

  // Check the signals in lifecycle order; the first that resolves wins.
  const candidates = [
    signals.opportunityType,
    signals.serviceType,
    signals.serviceFamily,
    signals.transactionType,
  ];
  for (const value of candidates) {
    if (!value) continue;
    if (SELL_SIGNALS.has(value)) return "sell";
    if (BUY_SIGNALS.has(value)) return "buy";
  }
  return null;
}

const SIDE_LABELS: Record<RealtySide, string> = {
  buy: "Buy-side",
  sell: "Sell-side",
};

/** Short pill/chip label for a side, e.g. "Buy-side". */
export function realtySideLabel(side: RealtySide): string {
  return SIDE_LABELS[side];
}

// Nate's feedback (2026-07-01): "instead of Realty Transaction, the name
// should be Listing, and then the address." The product-taxonomy nouns
// (vocabulary-system-v1: Listing = sell side, Home Purchase = buy side)
// replace the generic humanized transaction_type wherever the side is
// known. Non-realty callers keep their existing humanize() fallback.
const SIDE_NOUNS: Record<RealtySide, string> = {
  sell: "Listing",
  buy: "Home Purchase",
};

/** Product noun for a realty side: "Listing" (sell) / "Home Purchase" (buy). */
export function realtySideNoun(side: RealtySide): string {
  return SIDE_NOUNS[side];
}

/**
 * Display title for a transaction — the actual product the household
 * bought, not the workflow's internal type tag. Grant, 2026-08-10: a
 * Processing row read "Mortgage Loan Progression" — the humanized
 * `transaction_type` slug — when it should say what the loan actually is.
 *
 * Priority: the realty product noun when the side is known ("Listing" /
 * "Home Purchase") · else the linked service record's product label
 * ("Purchase Mortgage", "Private Money Loan", "Home Policy", …) via the
 * same SERVICE_TYPE_LABEL map every other product-noun surface uses ·
 * else the humanized transaction_type as a last resort, for the rare
 * record with no linked service record. One helper so the board card,
 * the list row, the detail header, and the tasks page all agree.
 */
export function transactionDisplayNoun(
  transactionType: string,
  side: RealtySide | null,
  serviceType?: string | null,
): string {
  // A rental is named for the rental, not for the side's sale noun
  // (2026-09-22): "Home Rental" / "Rental Listing", never "Home Purchase".
  if (side && isRentalServiceType(serviceType)) return rentalNoun(side);
  if (side) return SIDE_NOUNS[side];
  if (serviceType) return humanizeServiceType(serviceType);
  return transactionType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

// REWORK (Grant, 2026-08-11): Shopping / Pre Approved / Listing Prep moved
// off the Processing board and onto the Opportunity board — they're
// pre-transaction engagement, not delivery work. That leaves Processing's
// own first column, "Listed", as the correct sell-side entry: no override
// needed there. Buy-side now needs one instead — the combined board's
// first column is a sell-side term ("Listed"), so a won Buyer Consultation
// (which only happens once Shopping + Pre Approved already ran their
// course as Opportunity stages, meaning the buyer is under contract) must
// not silently land on it. It enters at "Opening Escrow", the first real
// buy-side delivery column.
//
// Rentals (2026-09-22) run on their own board, whose buy-side (tenant)
// entry is "Tenant Applied": a tenant consultation is won when
// the client applies for a home, the rental twin of going under
// contract. The first candidate present on the board wins, so the sale
// board is unaffected (it has no application column) and the rental
// board is unaffected by the sale slug (it has no escrow column).
const BUY_SIDE_ENTRY_STAGE_SLUGS = ["opening_escrow", "tenant_applied"] as const;

/**
 * Pick the entry stage for a new Realty transaction from its resolved
 * stage list, honoring the buy/sell side. Falls back to the first stage
 * when the side is unknown or the preferred slug isn't a column on this
 * board (e.g. a domain-owner override removed it).
 */
export function realtyEntryStageSlug(
  side: RealtySide | null,
  stageSlugs: readonly string[],
): string | undefined {
  if (side === "buy") {
    for (const slug of BUY_SIDE_ENTRY_STAGE_SLUGS) {
      if (stageSlugs.includes(slug)) return slug;
    }
  }
  return stageSlugs[0];
}

/**
 * Compose a short one-line property address ("123 E Main St, Gilbert")
 * from the realty_listings / realty_purchases columns. Returns null when
 * there's no street line — a bare city isn't an address worth leading
 * with.
 */
export function formatPropertyAddress(parts: {
  line1?: string | null;
  city?: string | null;
}): string | null {
  const line1 = parts.line1?.trim();
  if (!line1) return null;
  const city = parts.city?.trim();
  return city ? `${line1}, ${city}` : line1;
}
